import { useState, useCallback, useEffect } from 'react';
import { message as antdMessage } from 'antd';
import { uploadFile, uploadFilesBulk, deleteFileUpload, linkFileUploads, unlinkFileUploads } from '../Components/Messages/MessagesServices';
import { validateFileAgainstPolicy, formatBytes } from '../Utils/fileUploadPolicy';
import { resolveBackendMessage } from '../Utils/apiMessages';
import { isImageFile, generateThumbnail, processUploadedFile, getBase64, pickImageNative } from '../Utils/nativeFileUpload';

/**
 * Custom hook for managing file uploads in MessageFooter
 * Handles single/bulk uploads, camera/gallery selection, and conversation linking
 */
export const useFileUpload = ({ conversationId, initialFileList = [], policy, accept, isNative, t, handleError }) => {
    const [fileList, setFileList] = useState(initialFileList);
    const [uploading, setUploading] = useState(false);
    const [uploadingCamera, setUploadingCamera] = useState(false);
    const [uploadingGallery, setUploadingGallery] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewImage, setPreviewImage] = useState('');

    // Sync fileList with initialFileList when conversation changes
    useEffect(() => {
        setFileList(initialFileList);
    }, [conversationId]);

    // Helper function to show validation error messages
    const showValidationError = useCallback((validation) => {
        const code = validation?.code;
        if (code === 'extension') {
            const allowed = (validation.details?.allowed || []).join(', ');
            antdMessage.error(t('file_type_not_allowed', { exts: allowed }));
        } else if (code === 'size') {
            const max = formatBytes(validation.details?.maxSize);
            antdMessage.error(t('file_too_large_max', { max }));
        } else if (code === 'name') {
            const max = validation.details?.maxName;
            antdMessage.error(t('filename_too_long_max', { max }));
        } else {
            antdMessage.error(t('upload_failed'));
        }
    }, [t]);

    const handleFileUpload = useCallback(async (file) => {
        // Validate against upload policy first
        const validation = validateFileAgainstPolicy(file, policy);
        if (!validation.valid) {
            showValidationError(validation);
            return null;
        }
        setUploading(true);
        try {
            // Upload to API
            const response = await uploadFile(file);
            const fileData = response?.data;

            // Auto-link to conversation if conversationId provided
            if (conversationId && fileData?.id) {
                await linkFileUploads(conversationId, [fileData.id]);
            }

            setUploading(false);

            // Use utility function for consistent file processing (passes isNative for conditional thumbnail generation)
            return await processUploadedFile(file, fileData, isNative);
        } catch (error) {
            setUploading(false);
            // Use handleError for API errors with message_code support
            handleError(error);
            return null;
        }
    }, [conversationId, policy, t, handleError]);

    const handleBulkUpload = useCallback(async (incomingFiles = []) => {
        const files = Array.from(incomingFiles || []);
        if (!files.length) return;

        // Enforce UI cap (consistent with add-tile showing only when < 8)
        const remainingSlots = Math.max(0, 8 - fileList.length);
        if (remainingSlots === 0) {
            antdMessage.warning(t('max_attachments_reached'));
            return;
        }

        // Validate against policy
        const valid = [];
        const invalid = [];
        for (const f of files) {
            try {
                const res = validateFileAgainstPolicy(f, policy);
                if (res.valid) {
                    valid.push(f);
                } else {
                    invalid.push({ file: f, reason: res });
                }
            } catch (e) {
                invalid.push({ file: f, reason: { code: 'unknown' } });
            }
        }

        // Notify invalids succinctly
        if (invalid.length) {
            showValidationError(invalid[0]?.reason);
        }

        if (!valid.length) return;

        const toUpload = valid.slice(0, remainingSlots);
        if (toUpload.length < valid.length) {
            antdMessage.warning(t('some_files_skipped_due_to_limit'));
        }

        setUploading(true);
        try {
            const nameToFiles = new Map();
            for (const f of toUpload) {
                const arr = nameToFiles.get(f.name) || [];
                arr.push(f);
                nameToFiles.set(f.name, arr);
            }

            const bulk = await uploadFilesBulk(toUpload);
            const uploaded = Array.isArray(bulk?.uploaded) ? bulk.uploaded : [];
            const failed = Array.isArray(bulk?.failed) ? bulk.failed : [];

            const mapped = await Promise.all(uploaded.map(async (item) => {
                const nameKey = item?.filename || item?.original_filename || item?.name;
                const arr = nameToFiles.get(nameKey) || [];
                const original = arr.length ? arr.shift() : undefined;
                if (arr.length) nameToFiles.set(nameKey, arr); else nameToFiles.delete(nameKey);

                // Use utility functions for consistent native support
                const isImage = original ? isImageFile(original, item?.file_url) : false;

                // Only generate thumbnail for native OR fallback when no URL
                // On web, use API's file_url directly for performance
                const thumbUrl = (isNative || !item?.file_url)
                    ? (original ? await generateThumbnail(original, item?.file_url) : null)
                    : (isImage ? item.file_url : null);

                return {
                    uid: `-${Date.now()}-${Math.random().toString(16).slice(2)}`,
                    name: nameKey || original?.name || 'file',
                    status: 'done',
                    url: isImage ? item?.file_url : null,
                    thumbUrl: thumbUrl,
                    fileId: item?.id || null,
                    fileData: item,
                };
            }));

            // Auto-link all successfully uploaded files to conversation
            if (mapped.length && conversationId) {
                const uploadedIds = uploaded.map(item => item.id).filter(Boolean);
                await linkFileUploads(conversationId, uploadedIds);
            }

            if (mapped.length) {
                setFileList((prev) => [...prev, ...mapped]);
            }

            if (failed.length) {
                // Show first failure reason using backend message_code if provided
                const first = failed[0];
                const resolved = resolveBackendMessage(t, first);
                antdMessage.warning(resolved || t('some_files_failed_to_upload'));
            }
        } catch (error) {
            handleError(error);
        } finally {
            setUploading(false);
        }
    }, [fileList.length, policy, handleError, t, conversationId]);

    // Direct camera capture for native platforms
    const handleCameraClick = useCallback(async () => {
        if (!isNative) return;

        setUploadingCamera(true);
        try {
            const file = await pickImageNative({
                source: 'CAMERA',
                allowEditing: false,
                quality: 100
            });

            if (file) {
                const uploadedFile = await handleFileUpload(file);
                if (uploadedFile) {
                    setFileList((prev) => [...prev, uploadedFile]);
                }
            }
        } catch (error) {
            handleError(error);
        } finally {
            setUploadingCamera(false);
        }
    }, [isNative, handleFileUpload, handleError]);

    // Gallery/file selection for native platforms
    const handleGalleryClick = useCallback(async () => {
        if (!isNative) return;

        setUploadingGallery(true);
        try {
            const file = await pickImageNative({
                source: 'GALLERY',
                allowEditing: false,
                quality: 100
            });

            if (file) {
                const uploadedFile = await handleFileUpload(file);
                if (uploadedFile) {
                    setFileList((prev) => [...prev, uploadedFile]);
                }
            }
        } catch (error) {
            handleError(error);
        } finally {
            setUploadingGallery(false);
        }
    }, [isNative, handleFileUpload, handleError]);

    const handlePreview = useCallback(async (file) => {
        if (file.url) {
            setPreviewImage(file.url);
            setPreviewOpen(true);
            return;
        }

        if (!file.preview && file.originFileObj) {
            file.preview = await getBase64(file.originFileObj);
        }
        setPreviewImage(file.preview);
        setPreviewOpen(true);
    }, []);

    const handleChange = useCallback(async (info, file) => {
        if (info.file.status === 'removed') {
            try {
                // If we have a backend-uploaded fileId, unlink and delete it from server
                const target = fileList.find(f => f.uid === file.uid);
                const fileId = target?.fileId || file?.fileId;
                if (fileId) {
                    // Unlink from conversation first
                    if (conversationId) {
                        await unlinkFileUploads(conversationId, [fileId]);
                    }
                    // Then delete the upload
                    await deleteFileUpload(fileId);
                }
            } catch (err) {
                // Swallow error but still update UI; server cleanup can be retried later if needed
                handleError(err);
            } finally {
                setFileList((prev) => prev.filter(item => item.uid !== file.uid));
            }
        }
    }, [fileList, conversationId, handleError]);

    return {
        fileList,
        setFileList,
        uploading,
        uploadingCamera,
        uploadingGallery,
        previewOpen,
        previewImage,
        handleFileUpload,
        handleBulkUpload,
        handleCameraClick,
        handleGalleryClick,
        handlePreview,
        handleChange,
        setPreviewOpen,
        setPreviewImage
    };
};
